<script lang="ts">
  import Badge from './Badge.svelte';
  import type { Snippet } from 'svelte';

  type BadgeType = {
    label: string;
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default' | 'subscription' | 'bill';
    size?: 'xs' | 'sm' | 'md' | 'lg';
  };

  let {
    title,
    badges = [],
    description,
    selected = false,
    disabled = false,
    showCheckbox = false,
    showDetailsToggle = false,
    onToggleSelect,
    rightContent,
    children
  }: {
    title: string;
    badges?: BadgeType[];
    description?: string;
    selected?: boolean;
    disabled?: boolean;
    showCheckbox?: boolean;
    showDetailsToggle?: boolean;
    onToggleSelect?: () => void;
    rightContent?: Snippet;
    children?: Snippet;
  } = $props();

  let showDetails = $state(false);
</script>

<div class="suggestion-card" class:selected={selected} class:disabled={disabled}>
  {#if showCheckbox}
    <div class="selection-checkbox">
      <input
        type="checkbox"
        checked={selected}
        onchange={onToggleSelect}
        {disabled}
      />
    </div>
  {/if}

  <div class="suggestion-header">
    <div class="suggestion-title-row">
      <h3 class="suggestion-name">{title}</h3>
      {#if showDetailsToggle}
        <button
          class="details-toggle"
          onclick={() => showDetails = !showDetails}
          type="button"
          title={showDetails ? 'Hide details' : 'Show details'}
        >
          {showDetails ? '▲' : '▼'}
        </button>
      {/if}
    </div>

    {#if badges.length > 0}
      <div class="suggestion-badges">
        {#each badges as badge}
          <Badge variant={badge.variant || 'default'} size={badge.size || 'xs'}>
            {badge.label}
          </Badge>
        {/each}
      </div>
    {/if}

    {#if description}
      <p class="description">{description}</p>
    {/if}

    {#if showDetailsToggle && showDetails && children}
      <div class="details-content">
        {@render children()}
      </div>
    {/if}
  </div>

  {#if rightContent}
    <div class="suggestion-right">
      {@render rightContent()}
    </div>
  {/if}
</div>

<style lang="scss">
  .suggestion-card {
    position: relative;
    background: var(--color-background);
    border-bottom: 1px solid var(--color-border-light);
    border-left: 3px solid var(--color-warning);
    padding: var(--spacing-sm) var(--spacing-md);
    transition: all var(--transition-fast);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-md);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--color-background-secondary);
    }

    &.selected {
      background: var(--color-primary-light);
      border-left-color: var(--color-primary);
    }

    &.disabled {
      opacity: var(--opacity-disabled);
      pointer-events: none;
    }
  }

  .selection-checkbox {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }

  .suggestion-header {
    flex: 1;
    min-width: 0;
  }

  .suggestion-title-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    margin-bottom: 2px;
  }

  .suggestion-name {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0;
  }

  .details-toggle {
    background: none;
    border: none;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
    cursor: pointer;
    padding: var(--spacing-xs);
    line-height: 1;
    transition: color var(--transition-fast);
    flex-shrink: 0;

    &:hover {
      color: var(--color-text-primary);
    }
  }

  .suggestion-badges {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
    margin-bottom: var(--spacing-xs);
  }

  .description {
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    margin: 0;
    line-height: var(--line-height-tight);
  }

  .details-content {
    margin-top: var(--spacing-sm);
  }

  .suggestion-right {
    flex: 0 0 auto;
  }

  @media (max-width: 1024px) {
    .suggestion-card {
      flex-direction: column;
      align-items: stretch;
      gap: var(--spacing-md);
    }

    .suggestion-right {
      padding: var(--spacing-md) 0;
      border-top: 1px solid var(--color-warning-border);
      border-bottom: 1px solid var(--color-warning-border);
    }
  }

  @media (max-width: 640px) {
    .suggestion-card {
      padding: var(--spacing-xs) var(--spacing-sm);
      gap: var(--spacing-sm);
    }
  }
</style>
